import React, { useEffect, useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import notFound from "../../containers/Contacts/Table/NotFoundData"
import TableSkeletonRows from "../../components/Skeleton/TableSkeletonRows"
import { handleTableId } from "../../helpers/helper"
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { useDispatch, useSelector } from "react-redux"
import { removeAffiliate, resendAffiliateInvite } from "../../features/affiliates/affiliates"
import { useNavigate } from "react-router-dom"
import UserStatusCell from "../Contacts/Table/Contact/StatusCell"
import { StorageGetItem, USER } from "../../lib/storage"
import SortableTableHeader from "../../components/Table/SortableTableHeader"
import PopupWithActions from "../../components/Popup/PopupWithAction"
import useAffiliateStatusChange from "./useAffiliateStatusChange"
import {
  AFFILIATE_STATUS_MAP,
  AFFILIATE_STATUS_OPTIONS,
  PENDING_INVITE_BADGE,
  statusConfirmTitle,
  statusConfirmMessage,
} from "./affiliateStatus"
import { LuPencil, LuRotateCw, LuTrash2 } from "react-icons/lu"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { AFFILIATE_DELETE_REASSIGN } from "../../config/featureFlagKeys"
import ReassignFilesModal from "./ReassignFilesModal"
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../../components/UiKit/UiKitContext"
import { failureOf } from "../../utils/errorHelpers"

const headerCellSx = {
  fontSize: '13px', textTransform: 'uppercase', fontWeight: 600,
  color: 'text.secondary', backgroundColor: 'var(--color-bg-subtle)',
  borderBottom: 1, borderColor: 'divider',
  px: '10px', py: 0, verticalAlign: 'middle', whiteSpace: 'nowrap', lineHeight: 1,
}

const bodyCellSx = {
  fontSize: '0.875rem', fontWeight: 400,
  borderBottom: 1, borderColor: 'divider',
  color: 'text.primary', px: '10px', py: 0, verticalAlign: 'middle',
}

const AffiliatesTable = ({ data, setIsUpdated, isUpdated, sortField, sortOrder, onSort }) => {
  const [openDeleteTaskModal, setOpenDeleteTaskModal] = useState("")
  // { affiliate, message } while the agent being deleted still holds files (WS #2303654)
  const [reassign, setReassign] = useState(null)
  const reassignEnabled = useSelector(selectFeatureFlag(AFFILIATE_DELETE_REASSIGN))
  const [userRole, setUserRole] = useState('')
  const [statusMenu, setStatusMenu] = useState({ anchorEl: null, rowId: null })
  const statusChange = useAffiliateStatusChange({
    onSuccess: () => setIsUpdated(!isUpdated),
  })
  const { triggerLoading, isBarLoading } = useMyContextLoading()
  const { confirm: kitConfirm } = useUiKit()
  const showSkeleton = isBarLoading && !(data?.length > 0)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  const deleteAffiliate = async (affiliate, reassignTo) => {
    triggerLoading(true)
    try {
      const response = await dispatch(removeAffiliate(reassignTo ? { userId: affiliate?._id, reassignTo } : affiliate?._id))
      if (response?.payload?.data?._id) {
        setIsUpdated(!isUpdated)
        showSnackBar("success", "Affiliate successfully deleted")
      } else if (reassignEnabled && response?.payload?.code === 'agentHasFiles') {
        setReassign({ affiliate, message: response.payload.message })
      } else if (reassignEnabled && reassignTo && response?.payload?.message) {
        showSnackBar("error", response.payload.message)
      } else {
        showSnackBar("error", "Something went wrong, please try again")
      }
    } catch (error) {
      console.error(error)
    }
    triggerLoading(false)
  }

  const deleteAffiliateOrThrow = async (affiliate) => {
    const response = await dispatch(removeAffiliate(affiliate?._id))
    if (response?.payload?.data?._id) {
      setIsUpdated(!isUpdated)
      showSnackBar("success", "Affiliate successfully deleted")
      return
    }
    if (reassignEnabled && response?.payload?.code === 'agentHasFiles') {
      setReassign({ affiliate, message: response.payload.message })
      return
    }
    throw failureOf(response, "Something went wrong, please try again")
  }

  const handleResendInvite = async (userId) => {
    triggerLoading(true)
    try {
      const response = await dispatch(resendAffiliateInvite(userId))
      if (response?.payload?.data?.success) {
        showSnackBar("success", "Invitation email has been resent")
      } else {
        showSnackBar("error", response?.payload || "Failed to resend invitation")
      }
    } catch (error) {
      console.error(error)
    }
    triggerLoading(false)
  }

  const closeStatusMenu = () => setStatusMenu({ anchorEl: null, rowId: null })

  const openStatusMenu = (event, rowId) => {
    if (statusChange.busy) return
    // PopupWithActions attaches a native document click listener that closes the popup on
    // any outside click; without this stop it would immediately close the popup we are
    // switching to when the admin clicks another row's badge.
    event.stopPropagation()
    const anchorEl = event.currentTarget
    setStatusMenu((prev) =>
      prev.rowId === rowId ? { anchorEl: null, rowId: null } : { anchorEl, rowId }
    )
  }

  const handleStatusSelect = (row, status) => {
    closeStatusMenu()
    statusChange.requestChange({
      userId: row?._id,
      status,
      currentStatus: row?.affiliateStatus || 'active',
    })
  }

  const NotFound = notFound

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Affiliate Id</TableCell>
            <TableCell sx={headerCellSx}>
              <SortableTableHeader
                label="Name"
                field="name"
                currentSortField={sortField}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableCell>
            <TableCell sx={headerCellSx}>
              <SortableTableHeader
                label="Email"
                field="email"
                currentSortField={sortField}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableCell>
            <TableCell sx={headerCellSx}>Contact Groups</TableCell>
            {userRole === 'customer' && (
              <TableCell sx={headerCellSx}>Senior Affiliate</TableCell>
            )}
            <TableCell sx={headerCellSx}>Status</TableCell>
            <TableCell sx={headerCellSx}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {showSkeleton ? (
            <TableSkeletonRows rows={8} colSpan={userRole === 'customer' ? 7 : 6} />
          ) : data?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={userRole === 'customer' ? 7 : 6} sx={{ p: 0, border: 'none' }}>
                <NotFound />
              </TableCell>
            </TableRow>
          ) : (
            data?.map((row) => {
              const removeFunc = () => deleteAffiliate(row)
              return (
                <TableRow key={row._id} sx={{ height: 75 }}>
                  <TableCell sx={bodyCellSx}>
                    <span className="text-primary-main font-semibold">
                      {handleTableId(row?._id)}
                    </span>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {row?.firstName} {row?.lastName}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {row?.email}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {row?.contactGroups?.map((contactGroup) => (
                      <div key={contactGroup._id}>{contactGroup.name}</div>
                    ))}
                  </TableCell>
                  {userRole === 'customer' && (
                    <TableCell sx={bodyCellSx}>
                      <div className="capitalize">
                        {row?.affiliateManager === true
                          ? 'Yes'
                          : row?.affiliateManager === false
                            ? 'No'
                            : 'Not set'}
                      </div>
                    </TableCell>
                  )}
                  <TableCell sx={bodyCellSx}>
                    <div className="flex items-center gap-2">
                      <div
                        className="cursor-pointer"
                        onClick={(e) => openStatusMenu(e, row?._id)}
                      >
                        <UserStatusCell
                          statusObj={AFFILIATE_STATUS_MAP[row?.affiliateStatus] || AFFILIATE_STATUS_MAP.active}
                          arrow
                          isOpen={statusMenu.rowId === row?._id}
                        />
                      </div>
                      {!row?.isPassword && (
                        <UserStatusCell statusObj={PENDING_INVITE_BADGE} />
                      )}
                    </div>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <div className="flex items-center justify-end gap-3 w-full pr-1">
                      {!row?.isPassword && (
                        <div className="cursor-pointer" title="Resend Invite" onClick={() => handleResendInvite(row?._id)}>
                          <LuRotateCw size={18} className="text-text-secondary" />
                        </div>
                      )}
                      <div className="cursor-pointer" onClick={() => navigate(`/affiliates/edit/${row?._id}`)}>
                        <LuPencil size={18} className="text-text-secondary" />
                      </div>
                      <div className="cursor-pointer" title="Delete affiliate" onClick={() => setOpenDeleteTaskModal(row?._id)}>
                        <LuTrash2 size={18} className="text-text-secondary hover:text-error-main" />
                      </div>
                      {openDeleteTaskModal === row?._id && (kitConfirm ? (
                        <ConfirmDialog
                          open={Boolean(openDeleteTaskModal)}
                          onClose={() => setOpenDeleteTaskModal("")}
                          title={`Delete ${[row?.firstName, row?.lastName].filter(Boolean).join(' ') || 'this affiliate'}?`}
                          impact={["The affiliate account is removed from the affiliate list.", "This cannot be undone."]}
                          confirmLabel="Delete affiliate"
                          tone="danger"
                          onConfirm={() => deleteAffiliateOrThrow(row)}
                        />
                      ) : (
                        <ConfirmModal
                          leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
                          title="Are you sure to delete the Affiliate?"
                          modalButtonTitle={"Delete"}
                          classes=""
                          open={openDeleteTaskModal}
                          setOpen={setOpenDeleteTaskModal}
                          functionButton={removeFunc}
                        >
                          You will not be able to restore it later
                        </ConfirmModal>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
      <PopupWithActions
        anchor={statusMenu.anchorEl}
        open={Boolean(statusMenu.anchorEl)}
        handleClose={closeStatusMenu}
      >
        {AFFILIATE_STATUS_OPTIONS.map((option) => {
          const row = data?.find((r) => r?._id === statusMenu.rowId)
          const isCurrent = (row?.affiliateStatus || 'active') === option.value
          return (
            <div
              key={option.value}
              onClick={() => !isCurrent && !statusChange.busy && handleStatusSelect(row, option.value)}
              className={`p-[14px] rounded-md capitalize ${isCurrent || statusChange.busy
                ? 'text-text-disabled cursor-default'
                : 'cursor-pointer text-text-primary hover:bg-surface'}`}
            >
              Set {option.value}
            </div>
          )
        })}
      </PopupWithActions>
      {statusChange.pending && (
        <ConfirmModal
          title={statusConfirmTitle(statusChange.targetStatus)}
          modalButtonTitle="Confirm"
          open={statusChange.pending}
          setOpen={(val) => !val && statusChange.cancel()}
          functionButton={statusChange.confirm}
        >
          {statusConfirmMessage({ status: statusChange.targetStatus, isOwner: statusChange.isOwner })}
        </ConfirmModal>
      )}
      {reassign && (
        <ReassignFilesModal
          affiliate={reassign.affiliate}
          message={reassign.message}
          onCancel={() => setReassign(null)}
          onConfirm={(newOwner) => deleteAffiliate(reassign.affiliate, newOwner)}
        />
      )}
    </TableContainer>
  )
}

export default AffiliatesTable
